import type { Program } from "@project-serum/anchor0.20.1";
import { Provider } from "@project-serum/anchor0.20.1";
import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Zo } from "@zero_one/client";
import { Cluster, createProgram, Margin, State, ZO_MAINNET_STATE_KEY } from "@zero_one/client";
import { useEffect, useMemo, useState } from "react";

import { useNetworkConfig } from "../../contexts/app/config";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import type { MarginAccount, MarginOpenOrder, MarginPerpetualPosition, MarginPosition } from "../../types/margin";
import { getMarginPositionIcon } from "../../utils/margin";
import { CONFIRM_OPTIONS } from "../../utils/network";
import { getTokenByMint } from "../../utils/tokenInfo";
import { shortenAddress } from "../../utils/utils";
import { useConnectedWallet } from "../wallet/useConnectedWallet";

const getZeroOnePositions = async (program: Program<Zo>) => {
  const state: State = await State.load(program, ZO_MAINNET_STATE_KEY);
  const margin: Margin = await Margin.load(program, state);
  const userPositions: MarginPerpetualPosition[] = [];
  for (const position of margin.positions) {
    const { marketKey, coins, pCoins, isLong, realizedPnL } = position;
    if (!coins.number) {
      continue;
    }
    const { assetDecimals, indexPrice } = state.markets[marketKey];
    const symbol = state.data.perpMarkets.find((m) => m.symbol === marketKey)?.oracleSymbol ?? marketKey;

    userPositions.push({
      kind: "01 Exchange",
      market: marketKey,
      side: isLong ? "long" : "short",
      positionSize: coins.number,
      notionalSize: coins.decimal.mul(indexPrice.decimal).toNumber(),
      avgEntryPrice: pCoins.decimal.add(realizedPnL.decimal).div(coins.decimal).toNumber(),
      breakEvenPrice: 0,
      unrealizedPnl: margin.positionPnL(position).toNumber(),
      baseDecimals: assetDecimals,
      symbol,
      logoURI: getMarginPositionIcon(symbol),
    });
  }

  const userOpenOrders: MarginOpenOrder[] = [];
  for (const order of margin.orders) {
    const { marketKey, coins, long, price } = order;
    const { assetDecimals, indexPrice } = state.markets[marketKey];
    const symbol = state.data.perpMarkets.find((m) => m.symbol === marketKey)?.oracleSymbol ?? marketKey;

    userOpenOrders.push({
      price: price.number,
      kind: "01 Exchange",
      size: coins.decimal.mul(indexPrice.decimal).toNumber(),
      amount: coins.number,
      side: long ? "buy" : "sell",
      symbol,
      logoURI: getMarginPositionIcon(symbol),
      market: marketKey,
      baseDecimals: assetDecimals,
    });
  }

  let totalEarnedPerYear = 0;

  const userBalances: MarginPosition[] = [];
  for (const key in margin.balances) {
    const net = margin.balances[key].number;
    if (!net) {
      continue;
    }
    const { mint, borrowsApy, supplyApy, symbol, indexPrice } = state.assets[key];
    const logoURI = getTokenByMint(mint.toBase58())?.logoURI;

    let borrows = 0;
    let deposits = 0;
    if (net > 0) {
      deposits = net;
      totalEarnedPerYear += (net * indexPrice.number * supplyApy) / 100;
    } else if (net < 0) {
      borrows -= net;
      totalEarnedPerYear += (net * indexPrice.number * borrowsApy) / 100;
    }

    userBalances.push({
      mint: mint.toBase58(),
      logoURI,
      deposit: deposits,
      borrow: borrows,
      net,
      borrowApy: borrowsApy,
      depositApy: supplyApy,
      inOrders: 0,
      symbol: symbol,
      unsettled: 0,
    });
  }

  const leverage = margin.totalPositionNotional.div(margin.weightedCollateralValue).toNumber();
  const collateralRatio = margin.marginFraction.sub(margin.maintenanceMarginFraction).toNumber() * 100;
  const equity = margin.unweightedAccountValue.toNumber();

  const marginAccount: MarginAccount = {
    kind: "01 Exchange",
    pubkey: margin.pubkey,
    name: shortenAddress(margin.pubkey.toBase58()),
    balances: userBalances,
    orders: userOpenOrders,
    perpetuals: userPositions,
    leverage,
    collateralRatio,
    equity,
    interest: totalEarnedPerYear / equity,
  };
  return [marginAccount];
};

export function useZeroOne() {
  const wallet = useConnectedWallet();
  const { connection } = useConnection();
  const { connected } = useEnrichedWallet();
  const { network } = useNetworkConfig();

  const [refreshCounter, setRefreshCounter] = useState(0);
  const [marginAccounts, setMarginAccounts] = useState<MarginAccount[]>([]);

  const refreshZeroOne = () => {
    setRefreshCounter(refreshCounter + 1);
  };

  useEffect(() => {
    setMarginAccounts([]);
  }, [connected]);

  const zeroOneProgram = useMemo(() => {
    const cluster = network === "mainnet-beta" ? Cluster.Mainnet : network === "devnet" ? Cluster.Devnet : undefined;
    if (connected && wallet && cluster) {
      const provider = new Provider(connection, wallet, CONFIRM_OPTIONS);
      return createProgram(provider, cluster);
    }
  }, [connection, connected, network, wallet]);

  useEffect(() => {
    if (zeroOneProgram) {
      void getZeroOnePositions(zeroOneProgram)
        .then((zeroOnePositions) => {
          const formattedZeroOnePositions = zeroOnePositions
            .filter(
              (position) =>
                position.balances.length > 0 || position.orders.length > 0 || position.perpetuals.length > 0,
            )
            .map((position) => {
              return {
                ...position,
                interest: isNaN(position.interest) ? 0 : position.interest,
                leverage: isNaN(position.leverage) ? 0 : position.leverage,
              };
            });
          setMarginAccounts(formattedZeroOnePositions);
        })
        .catch((err) => {
          console.error("useZeroOne Error:", err);
          captureException(err);
          setMarginAccounts([]);
        });
    }
  }, [zeroOneProgram, refreshCounter]);
  return { zeroOneMarginAccounts: marginAccounts, refreshZeroOne };
}
